import { useEffect, useState } from 'react';
import {
  TIER_NAMES,
  MVP_SUBSCRIBER_TIERS,
  COMMITMENT_LABELS,
  getCataloguePricing,
  displayPrice,
  fmtUsd,
  termDiscountPercent,
  bestValueTerm,
  type Tier,
  type CommitmentTerm,
} from '../../lib/promo-codes';
import { fetchCatalogue, type Catalogue } from '../../lib/catalogue';
import type { StepProps } from '../OBWWizard';
import { portalOriginFor } from '../portal-origin';

// Subscribe step — Early Adopter / rack commitment-based pricing.
// (Rebuilt 10 June 2026 per Jeff's verbal pricing instruction; supersedes the
//  earlier flat monthly/annual toggle. See promo-codes.ts for the model + the
//  Business_Reference_v27 conflict note.)
//
//   - Two tiers visible to subscribers (NexaGrowth + NexaSuccess); NexaContext
//     (T3) is post-MVP and excluded (admin actors still see all three).
//   - Commitment selector: Monthly (full rack) / 3 months / 12 months (EA).
//   - ⭐ EVERY NUMBER IS THE CATALOGUE'S — the same as the subscriber fork (ADR-073 Am.5 §A5.6;
//     brought to this fork 7 October 2026). The prices, each term's "N% off" and the "Best value"
//     badge come from the journey's zone's `GET /portal/catalogue` (`portalOriginFor(data)`), never
//     from a table here. This fork had kept "50% off" for both committed terms after the catalogue
//     moved the 3-month term to 25% (Jeff, 6 October 2026: consistent "across the entire site").
//     Until the catalogue loads the step says so; it never shows a guessed price.
//   - Display only — real Stripe processing is LAUNCH (5d-4).

interface PlanDef {
  tier: Tier;
  tag: string;
  recommended?: boolean;
  features: string[];
}

const PLAN_DEFS: Record<Tier, PlanDef> = {
  T1: {
    tier: 'T1',
    tag: 'Current-month Customer Journey Intelligence — no marketing expertise required.',
    recommended: true,
    features: [
      'Monthly Effectiveness Scores (A–F): Discovery, Website, Social',
      'Current-month Growth Effectiveness Report',
      'Availability monitoring with smart email alerts',
      'Fix Guide — prescriptive remediation steps',
      'Loyalty tools',
    ],
  },
  T2: {
    tier: 'T2',
    tag: 'Acquisition & loyalty — 12-month growth narrative with AI interpretation.',
    features: [
      'Everything in NexaGrowth',
      '12 months of historical trend context',
      'Digital breadcrumb attribution across the journey',
      'AI-generated executive insight on what changed',
      'Reach, discovery and effectiveness over time',
    ],
  },
  T3: {
    tier: 'T3',
    tag: 'Contextual intelligence (post-MVP).',
    features: [
      'Everything in NexaSuccess',
      'GEO Scoring (Generative Engine Optimisation)',
      'Contextual Event Attribution',
    ],
  },
};

const COMMITMENT_ORDER: CommitmentTerm[] = ['monthly', 'quarterly', 'annual'];

export function StepSubscribe({ data, onNext, isRequired, actor, nextStepId }: StepProps) {
  const initialTier = (data?.selectedTier as Tier) ?? 'T1'; // T1 is default
  const initialCommitment = (data?.commitment as CommitmentTerm) ?? 'annual';
  const origin = portalOriginFor(data);

  const [selectedTier, setSelectedTier] = useState<Tier | null>(initialTier);
  const [commitment, setCommitment] = useState<CommitmentTerm>(initialCommitment);
  const [error, setError] = useState('');
  // ⛔ NO FALLBACK PRICE: until the zone's catalogue resolves the step says it is loading; if it
  // fails, it says so and offers a retry.
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [catalogueFailed, setCatalogueFailed] = useState(false);
  const [catalogueTry, setCatalogueTry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setCatalogueFailed(false);
    fetchCatalogue(origin)
      .then((cat) => { if (!cancelled) setCatalogue(cat); })
      .catch(() => { if (!cancelled) setCatalogueFailed(true); });
    return () => { cancelled = true; };
  }, [origin, catalogueTry]);

  // T3 (NexaContext) is post-MVP — subscribers see two tiers, admins see all.
  const visibleTiers: Tier[] = actor === 'nexa-admin' ? ['T1', 'T2', 'T3'] : MVP_SUBSCRIBER_TIERS;

  // The badge and each term's "N% off", from the catalogue — for the tier on screen.
  const badgeTier: Tier = selectedTier ?? 'T1';
  const best = catalogue ? bestValueTerm(catalogue, badgeTier) : null;
  const termPercent = (term: CommitmentTerm): number =>
    catalogue ? termDiscountPercent(getCataloguePricing(catalogue, badgeTier, term)) : 0;
  const maxPercent = Math.max(0, ...COMMITMENT_ORDER.map(termPercent));

  function handleNext() {
    setError('');
    if (!catalogue) { setError('Prices are still loading — please wait a moment.'); return; }
    if (isRequired && !selectedTier) { setError('Please select a plan to continue.'); return; }
    onNext({
      selectedTier,
      commitment,
      // Back-compat: map commitment → legacy billing axis for any older reader.
      billing: commitment === 'monthly' ? 'monthly' : 'annual',
      // D8 — no promo code is collected or sent: the promo capability is not built (Jeff, 5 October
      // 2026), so a code could only preview a discount the server would never bill.
      promoCode: null,
    });
  }

  /** One commitment choice's second line — what the term costs relative to rack, from the data. */
  const termSub = (term: CommitmentTerm): string => {
    const pct = termPercent(term);
    if (term === 'monthly' || pct <= 0) return 'Full price, no commitment';
    return `Early Adopter price — ${pct}% off, billed upfront`;
  };

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">Choose your plan</h2>
      <p className="obw-subtitle">
        {maxPercent > 0
          ? `Nexanomics is priced per domain. Commit to a term to lock the Early Adopter price — up to ${maxPercent}% off the rack rate — for the length of that term. You can change tier later from your account.`
          : 'Nexanomics is priced per domain. You can change tier later from your account.'}
      </p>

      {/* Commitment-term selector */}
      <div className="obw-commit-row" role="radiogroup" aria-label="Commitment term">
        {COMMITMENT_ORDER.map((term) => {
          const isSel = commitment === term;
          return (
            <button
              key={term}
              type="button"
              role="radio"
              aria-checked={isSel}
              className={`obw-commit-option${isSel ? ' selected' : ''}`}
              onClick={() => setCommitment(term)}
            >
              {best?.term === term && (
                <span className="obw-commit-best" data-testid="obw-best-value">
                  Best value{best.monthsFree > 0 ? ` · ${best.monthsFree} months free` : ''}
                </span>
              )}
              <span className="obw-commit-term">{COMMITMENT_LABELS[term]}</span>
              <span className="obw-commit-sub">{termSub(term)}</span>
            </button>
          );
        })}
      </div>

      {!catalogue && (
        catalogueFailed ? (
          <div className="portal-wizard-error" role="alert">
            We couldn’t load our prices just now.{' '}
            <button type="button" className="obw-btn-secondary" onClick={() => setCatalogueTry((n) => n + 1)}>
              Try again
            </button>
          </div>
        ) : (
          <p className="obw-subtitle" role="status" aria-live="polite" data-testid="obw-prices-loading">Loading prices…</p>
        )
      )}

      {catalogue && (
      <div
        className={`obw-plan-grid${selectedTier ? ' has-selection' : ''}`}
        role="radiogroup"
        aria-label="Plan selection"
      >
        {visibleTiers.map((tier) => {
          const def = PLAN_DEFS[tier];
          const pricing = getCataloguePricing(catalogue, tier, commitment);
          if (!pricing) {
            // Defined but not priced (NexaContext): shown to admins as coming soon, never priced.
            return (
              <div key={tier} className="obw-plan-card obw-plan-card-unpriced" aria-disabled="true" data-testid={`obw-plan-${tier}`}>
                <div className={`obw-plan-tier ${tier.toLowerCase()}`}>Tier {tier.slice(1)}</div>
                <div className="obw-plan-name">{TIER_NAMES[tier]}</div>
                <div className="obw-plan-tag">{def.tag}</div>
                <p className="obw-plan-billed">Coming soon</p>
              </div>
            );
          }
          // The one shared price (lib/promo-codes `displayPrice`) — the payment step shows exactly this.
          const { effectiveMonthly, termTotal, showStrike } = displayPrice(pricing, 0);
          const isSelected = selectedTier === tier;
          const showRecommended = def.recommended && actor !== 'nexa-admin';
          const billed = commitment === 'monthly'
            ? `Billed $${fmtUsd(effectiveMonthly)} each month`
            : `Billed $${fmtUsd(termTotal)} once for ${pricing.months} months`;
          return (
            <button
              key={tier}
              type="button"
              className={[
                'obw-plan-card',
                showRecommended ? 'recommended' : '',
                isSelected ? 'selected' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => setSelectedTier(tier)}
              aria-pressed={isSelected}
              role="radio"
              aria-checked={isSelected}
              data-testid={`obw-plan-${tier}`}
            >
              {showRecommended && <span className="obw-plan-rec">Recommended</span>}
              <div className={`obw-plan-tier ${tier.toLowerCase()}`}>Tier {tier.slice(1)}</div>
              <div className="obw-plan-name">{TIER_NAMES[tier]}</div>
              <div className="obw-plan-tag">{def.tag}</div>

              <div className="obw-plan-price">
                {showStrike && (
                  <span className="obw-plan-price-strike">${fmtUsd(pricing.rackMonthly)}</span>
                )}
                <span className={showStrike ? 'obw-plan-price-ea' : ''}>${fmtUsd(effectiveMonthly)}</span>
                <span className="obw-plan-period">/mo</span>
              </div>

              <p className="obw-plan-billed">{billed}</p>
              {pricing.isEa && (
                <p className="obw-plan-revert">Renews at ${fmtUsd(pricing.rackMonthly)}/mo (rack rate)</p>
              )}

              <div className="obw-plan-divider" />
              <ul className="obw-plan-features">
                {def.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>
      )}

      {/* D8 (Jeff, 5 October 2026) — the promo-code field is hidden until the promo capability is
          built; the old field previewed a discount from a local table that the server never billed. */}

      {error && <div className="portal-wizard-error" role="alert">{error}</div>}

      <div className="portal-wizard-nav">
        <button
          type="button"
          className="obw-btn-primary"
          onClick={handleNext}
          disabled={(isRequired && !selectedTier) || !catalogue}
        >
          {/* PL22 — reflect the REAL next step. "Continue to payment" only when payment
              actually follows: SOW pairs plan→payment, but the BOW's plan step is followed by
              the profile and a beta is never charged. Matches the subscriber fork. */}
          {nextStepId === 'payment' ? 'Continue to payment' : 'Continue'}
        </button>
      </div>
    </div>
  );
}
